<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Company Tax Calculation</h1>
            <p class="text-slate-400">Manage company tax calculations, filings, and compliance</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="showTaxEntryModal = true" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              Add Tax Entry
            </button>
            <button @click="generateTaxReport" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Generate Report
            </button>
          </div>
        </div>
      </div>

      <!-- Tax Overview Stats -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Total Income</p>
            <p class="text-3xl font-bold text-white">${{ taxStats.totalIncome.toLocaleString() }}</p>
            <p class="text-sm text-emerald-400">this year</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-red-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Tax Liability</p>
            <p class="text-3xl font-bold text-white">${{ taxStats.taxLiability.toLocaleString() }}</p>
            <p class="text-sm text-red-400">estimated</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Deductions</p>
            <p class="text-3xl font-bold text-white">${{ taxStats.totalDeductions.toLocaleString() }}</p>
            <p class="text-sm text-cyan-400">claimed</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Effective Rate</p>
            <p class="text-3xl font-bold text-white">{{ taxStats.effectiveRate }}%</p>
            <p class="text-sm text-purple-400">tax rate</p>
          </div>
        </div>
      </div>

      <!-- Tax Calculator Form -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Tax Calculator -->
        <div class="lg:col-span-2">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h2 class="text-xl font-semibold text-white mb-6">Tax Calculator</h2>
            
            <form @submit.prevent="calculateTax" class="space-y-6">
              <!-- Income Sources -->
              <div>
                <h3 class="text-lg font-medium text-white mb-4">Income Sources</h3>
                <div class="space-y-4">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-slate-300 mb-2">Business Income</label>
                      <input v-model="taxData.businessIncome" type="number" step="0.01" class="form-input w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-slate-300 mb-2">Investment Income</label>
                      <input v-model="taxData.investmentIncome" type="number" step="0.01" class="form-input w-full" />
                    </div>
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-slate-300 mb-2">Rental Income</label>
                      <input v-model="taxData.rentalIncome" type="number" step="0.01" class="form-input w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-slate-300 mb-2">Other Income</label>
                      <input v-model="taxData.otherIncome" type="number" step="0.01" class="form-input w-full" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Deductions -->
              <div>
                <h3 class="text-lg font-medium text-white mb-4">Deductions</h3>
                <div class="space-y-4">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-slate-300 mb-2">Business Expenses</label>
                      <input v-model="taxData.businessExpenses" type="number" step="0.01" class="form-input w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-slate-300 mb-2">Depreciation</label>
                      <input v-model="taxData.depreciation" type="number" step="0.01" class="form-input w-full" />
                    </div>
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-slate-300 mb-2">Employee Benefits</label>
                      <input v-model="taxData.employeeBenefits" type="number" step="0.01" class="form-input w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-slate-300 mb-2">Other Deductions</label>
                      <input v-model="taxData.otherDeductions" type="number" step="0.01" class="form-input w-full" />
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex items-center justify-end space-x-3 pt-4">
                <button type="button" @click="resetCalculator" class="btn-secondary">
                  Reset
                </button>
                <button type="submit" class="btn-primary">
                  Calculate Tax
                </button>
              </div>
            </form>

            <!-- Tax Calculation Results -->
            <div v-if="taxCalculation" class="mt-8 p-6 bg-slate-700/30 rounded-xl">
              <h3 class="text-lg font-medium text-white mb-4">Tax Calculation Results</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p class="text-sm text-slate-400">Total Income</p>
                  <p class="text-2xl font-bold text-white">${{ taxCalculation.totalIncome.toLocaleString() }}</p>
                </div>
                <div>
                  <p class="text-sm text-slate-400">Total Deductions</p>
                  <p class="text-2xl font-bold text-cyan-400">${{ taxCalculation.totalDeductions.toLocaleString() }}</p>
                </div>
                <div>
                  <p class="text-sm text-slate-400">Taxable Income</p>
                  <p class="text-2xl font-bold text-white">${{ taxCalculation.taxableIncome.toLocaleString() }}</p>
                </div>
                <div>
                  <p class="text-sm text-slate-400">Estimated Tax</p>
                  <p class="text-2xl font-bold text-red-400">${{ taxCalculation.estimatedTax.toLocaleString() }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tax Summary -->
        <div class="lg:col-span-1">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Tax Summary</h3>
            
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Gross Income</span>
                <span class="text-sm font-medium text-white">${{ taxSummary.grossIncome.toLocaleString() }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Deductions</span>
                <span class="text-sm font-medium text-cyan-400">-${{ taxSummary.deductions.toLocaleString() }}</span>
              </div>
              <div class="border-t border-slate-700/50 pt-2">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-slate-300">Taxable Income</span>
                  <span class="text-sm font-bold text-white">${{ taxSummary.taxableIncome.toLocaleString() }}</span>
                </div>
              </div>
              <div class="border-t border-slate-700/50 pt-2">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-slate-300">Federal Tax</span>
                  <span class="text-sm font-bold text-red-400">${{ taxSummary.federalTax.toLocaleString() }}</span>
                </div>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-slate-300">State Tax</span>
                <span class="text-sm font-bold text-red-400">${{ taxSummary.stateTax.toLocaleString() }}</span>
              </div>
              <div class="border-t border-slate-700/50 pt-2">
                <div class="flex items-center justify-between">
                  <span class="text-lg font-bold text-white">Total Tax</span>
                  <span class="text-lg font-bold text-red-400">${{ taxSummary.totalTax.toLocaleString() }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Quarterly Payments -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mt-6">
            <h3 class="text-lg font-semibold text-white mb-4">Quarterly Payments</h3>
            
            <div class="space-y-3">
              <div v-for="payment in quarterlyPayments" :key="payment.quarter" class="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                <div>
                  <p class="text-sm font-medium text-white">{{ payment.quarter }}</p>
                  <p class="text-xs text-slate-400">{{ payment.dueDate }}</p>
                </div>
                <div class="text-right">
                  <p class="text-sm font-medium text-white">${{ payment.amount.toLocaleString() }}</p>
                  <span :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    payment.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                    payment.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  ]">
                    {{ payment.status }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tax Documents -->
      <div class="mt-8">
        <h2 class="text-xl font-semibold text-white mb-6">Tax Documents</h2>
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Document</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Type</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Year</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="document in taxDocuments" :key="document.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-white">{{ document.name }}</div>
                    <div class="text-sm text-slate-400">{{ document.description }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ document.type }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ document.year }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      document.status === 'filed' ? 'bg-emerald-500/20 text-emerald-400' :
                      document.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    ]">
                      {{ document.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center space-x-2">
                      <button @click="viewDocument(document)" class="text-emerald-400 hover:text-emerald-300">
                        View
                      </button>
                      <button @click="downloadDocument(document.id)" class="text-cyan-400 hover:text-cyan-300">
                        Download
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Add Tax Entry Modal -->
      <div v-if="showTaxEntryModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Add Tax Entry</h3>
          
          <form @submit.prevent="submitTaxEntry" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Entry Type</label>
                <select v-model="taxEntryData.type" class="form-select w-full" required>
                  <option value="">Select Type</option>
                  <option value="income">Income</option>
                  <option value="deduction">Deduction</option>
                  <option value="expense">Expense</option>
                  <option value="payment">Payment</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Amount</label>
                <input v-model="taxEntryData.amount" type="number" step="0.01" class="form-input w-full" required />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <input v-model="taxEntryData.description" type="text" class="form-input w-full" required />
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Date</label>
                <input v-model="taxEntryData.date" type="date" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Category</label>
                <select v-model="taxEntryData.category" class="form-select w-full" required>
                  <option value="">Select Category</option>
                  <option value="business-income">Business Income</option>
                  <option value="investment-income">Investment Income</option>
                  <option value="rental-income">Rental Income</option>
                  <option value="business-expenses">Business Expenses</option>
                  <option value="depreciation">Depreciation</option>
                  <option value="employee-benefits">Employee Benefits</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Notes</label>
              <textarea v-model="taxEntryData.notes" class="form-input w-full" rows="3"></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showTaxEntryModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Add Entry
              </button>
            </div>
          </form>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

const userStore = useUserStore()

// Reactive data
const showTaxEntryModal = ref(false)
const taxCalculation = ref(null)

const taxStats = ref({
  totalIncome: 0,
  taxLiability: 0,
  totalDeductions: 0,
  effectiveRate: 0
})

const taxData = ref({
  businessIncome: 0,
  investmentIncome: 0,
  rentalIncome: 0,
  otherIncome: 0,
  businessExpenses: 0,
  depreciation: 0,
  employeeBenefits: 0,
  otherDeductions: 0
})

const taxEntryData = ref({
  type: '',
  amount: '',
  description: '',
  date: '',
  category: '',
  notes: ''
})

const quarterlyPayments = ref([])
const taxDocuments = ref([])

// Computed
const taxSummary = computed(() => {
  const grossIncome = taxData.value.businessIncome + taxData.value.investmentIncome + 
                     taxData.value.rentalIncome + taxData.value.otherIncome
  
  const deductions = taxData.value.businessExpenses + taxData.value.depreciation + 
                    taxData.value.employeeBenefits + taxData.value.otherDeductions
  
  const taxableIncome = Math.max(0, grossIncome - deductions)
  
  // Simplified tax calculation (this would be more complex in reality)
  const federalTax = taxableIncome * 0.21 // Corporate tax rate
  const stateTax = taxableIncome * 0.05 // State tax rate (varies by state)
  const totalTax = federalTax + stateTax
  
  return {
    grossIncome,
    deductions,
    taxableIncome,
    federalTax,
    stateTax,
    totalTax
  }
})

// Methods
const fetchTaxStats = async () => {
  try {
    const response = await fetch('/api/company/tax-stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      taxStats.value = data
    }
  } catch (error) {
    console.error('Error fetching tax stats:', error)
  }
}

const fetchQuarterlyPayments = async () => {
  try {
    const response = await fetch('/api/company/quarterly-payments', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      quarterlyPayments.value = data.payments || []
    }
  } catch (error) {
    console.error('Error fetching quarterly payments:', error)
  }
}

const fetchTaxDocuments = async () => {
  try {
    const response = await fetch('/api/company/tax-documents', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      taxDocuments.value = data.documents || []
    }
  } catch (error) {
    console.error('Error fetching tax documents:', error)
  }
}

const calculateTax = () => {
  taxCalculation.value = {
    totalIncome: taxData.value.businessIncome + taxData.value.investmentIncome + 
                taxData.value.rentalIncome + taxData.value.otherIncome,
    totalDeductions: taxData.value.businessExpenses + taxData.value.depreciation + 
                    taxData.value.employeeBenefits + taxData.value.otherDeductions,
    taxableIncome: Math.max(0, (taxData.value.businessIncome + taxData.value.investmentIncome + 
                                taxData.value.rentalIncome + taxData.value.otherIncome) - 
                               (taxData.value.businessExpenses + taxData.value.depreciation + 
                                taxData.value.employeeBenefits + taxData.value.otherDeductions)),
    estimatedTax: Math.max(0, (taxData.value.businessIncome + taxData.value.investmentIncome + 
                               taxData.value.rentalIncome + taxData.value.otherIncome) - 
                              (taxData.value.businessExpenses + taxData.value.depreciation + 
                               taxData.value.employeeBenefits + taxData.value.otherDeductions)) * 0.26
  }
}

const resetCalculator = () => {
  taxData.value = {
    businessIncome: 0,
    investmentIncome: 0,
    rentalIncome: 0,
    otherIncome: 0,
    businessExpenses: 0,
    depreciation: 0,
    employeeBenefits: 0,
    otherDeductions: 0
  }
  taxCalculation.value = null
}

const submitTaxEntry = async () => {
  try {
    const response = await fetch('/api/company/add-tax-entry', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(taxEntryData.value)
    })
    
    if (response.ok) {
      showTaxEntryModal.value = false
      taxEntryData.value = { type: '', amount: '', description: '', date: '', category: '', notes: '' }
      await fetchTaxStats()
    }
  } catch (error) {
    console.error('Error submitting tax entry:', error)
  }
}

const generateTaxReport = () => {
  // Generate tax report
}

const viewDocument = (document: any) => {
  // View document details
}

const downloadDocument = (documentId: number) => {
  // Download document
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchTaxStats(),
    fetchQuarterlyPayments(),
    fetchTaxDocuments()
  ])
})
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}
</style>
